import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, MessageCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const WHATSAPP_NUMBER = "919839417577"; // +91 9839417577

const COURSES = [
  "B.A. — Urdu",
  "B.A. — Hindi",
  "B.A. — Medieval History",
  "B.A. — Sociology",
  "B.A. — Sanskrit",
  "B.A. — Education",
  "B.A. — Economics",
  "B.A. — English",
  "B.A. — Fashion Designing",
  "M.A. — Urdu",
  "M.A. — Medieval History",
  "General Enquiry",
];

export default function ApplyDrawer({ open, onClose }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    course: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.course) return;
    setSending(true);
    const text =
      `*New Admission Enquiry — JAS Imambara Girls P.G. College*%0A%0A` +
      `*Name:* ${encodeURIComponent(form.name)}%0A` +
      `*Phone:* ${encodeURIComponent(form.phone)}%0A` +
      (form.email ? `*Email:* ${encodeURIComponent(form.email)}%0A` : "") +
      `*Course:* ${encodeURIComponent(form.course)}%0A` +
      (form.message ? `*Message:* ${encodeURIComponent(form.message)}%0A` : "") +
      `%0A_Sent from college website_`;

    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");

    setTimeout(() => {
      setSending(false);
      setForm({ name: "", phone: "", email: "", course: "", message: "" });
      onClose?.();
    }, 600);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-brand-primary/40 backdrop-blur-sm"
            onClick={onClose}
            data-testid="apply-drawer-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 380, damping: 36 }}
            className="fixed top-0 right-0 bottom-0 z-[61] w-full sm:w-[440px] bg-brand-ivory shadow-[-20px_0_60px_rgba(15,76,58,0.25)] flex flex-col overflow-hidden"
            data-testid="apply-drawer"
            role="dialog"
            aria-label="Apply for admission via WhatsApp"
          >
            {/* Header */}
            <div className="bg-brand-primary text-white px-6 py-5 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 text-brand-secondary text-xs uppercase tracking-[0.25em] font-semibold mb-1">
                  <MessageCircle className="w-3.5 h-3.5" /> Quick Apply
                </div>
                <h2 className="font-serif text-2xl leading-tight">
                  Send your details<br /> via WhatsApp
                </h2>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                data-testid="apply-drawer-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form
              onSubmit={onSubmit}
              data-testid="apply-drawer-form"
              className="flex-1 overflow-y-auto p-6 space-y-4"
            >
              <p className="text-sm text-brand-muted leading-relaxed">
                Fill in your details and click send — we'll open WhatsApp with a
                pre-filled message to our admissions team. They'll reply within
                a few hours.
              </p>

              <div>
                <Label htmlFor="ad-name">Full Name *</Label>
                <Input
                  id="ad-name"
                  name="name"
                  data-testid="ad-name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Your full name"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="ad-phone">Mobile Number *</Label>
                <Input
                  id="ad-phone"
                  name="phone"
                  data-testid="ad-phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="+91 9XXXXXXXXX"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="ad-email">Email (optional)</Label>
                <Input
                  id="ad-email"
                  name="email"
                  type="email"
                  data-testid="ad-email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@example.com"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Interested in *</Label>
                <Select
                  value={form.course}
                  onValueChange={(v) => setForm({ ...form, course: v })}
                >
                  <SelectTrigger data-testid="ad-course" className="mt-1">
                    <SelectValue placeholder="Select a course" />
                  </SelectTrigger>
                  <SelectContent>
                    {COURSES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="ad-msg">Your message (optional)</Label>
                <Textarea
                  id="ad-msg"
                  name="message"
                  data-testid="ad-message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Tell us about your previous education or any questions..."
                  rows={3}
                  className="mt-1"
                />
              </div>
            </form>

            {/* Footer CTA */}
            <div className="border-t border-brand-primary/10 bg-white px-6 py-4 flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-brand-muted hover:text-brand-primary px-3 py-2"
                data-testid="apply-drawer-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSubmit}
                disabled={sending || !form.name || !form.phone || !form.course}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-medium transition-all duration-300 bg-[#25D366] text-white hover:bg-[#1eba56] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="apply-drawer-send"
              >
                {sending ? "Opening WhatsApp..." : (
                  <>
                    Send via WhatsApp <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
